import { Auth, AuthMarketplace, logoutAuth } from '@spike/auth-model';
import AES from 'crypto-js/aes';
import CryptoJS from 'crypto-js';
import { Role } from '@spike/masterdata-model';

const authKey = `${process.env.REACT_APP_AUTH_KEY}`;
const secret = `${process.env.REACT_APP_AUTH_SECRET}`;
const calendarStorageKeys = [
    'dateCalendar',
    'calendarDate',
    'calendarWeek',
    'calendarMonth',
    'calendarBusinessArea',
    'calendarViewMode',
    'calendarSelectedStaffIdsByArea',
    'calendarSelectedRoomUnitIdsByArea'
];

const setLocalAuth = (auth: Auth) => {
    setAuth(auth, localStorage);
};

const setSessionAuth = (auth: Auth) => {
    setAuth(auth, sessionStorage);
};

const getLocalAuth = () => {
    return getAuth(localStorage);
};

const getSessionAuth = () => {
    return getAuth(sessionStorage);
};

const cleanLocal = () => {
    cleanAuth(localStorage);
    cleanCalendar(localStorage);
};

const cleanSession = () => {
    cleanAuth(sessionStorage);
    cleanCalendar(sessionStorage);
};

const cleanAuth = (storage: Storage) => {
    storage.removeItem(authKey);
};

const cleanCalendar = (storage: Storage) => {
    calendarStorageKeys.forEach(key => storage.removeItem(key));
};

const setAuth = (auth: Auth, storage: Storage) => {
    const encryptedAuth = AES.encrypt(JSON.stringify(auth), secret).toString();
    storage.setItem(authKey, encryptedAuth);
};

const getAuth = (storage: Storage): Auth | null => {
    try {
        //Remove old auth storage item previous multilocation
        storage.removeItem(authKey.slice(0, -1));

        const encryptedAuth = storage.getItem(authKey);

        if (encryptedAuth) {
            return JSON.parse(AES.decrypt(encryptedAuth, secret).toString(CryptoJS.enc.Utf8));
        } else {
            return null;
        }
    } catch (error) {
        cleanSession();
        cleanLocal();
        return null;
    }
};

export const saveInStorage = (auth: Auth, local: boolean): void => {
    if (local) {
        setLocalAuth(auth);
    } else {
        cleanLocal();
    }

    setSessionAuth(auth);
};

export const getFromStorage = (): Auth => {
    let auth = getSessionAuth();

    if (auth === null) {
        auth = getLocalAuth();
        if (auth !== null) {
            setSessionAuth(auth);
        }
    }

    if (auth === null) {
        return { ...logoutAuth };
    } else {
        return auth;
    }
};

export const removeFromStorage = (): void => {
    cleanSession();
    cleanLocal();
    clearImpersonation();
};

// Marks a tab signed in by an organization user as one of its staff. Kept in sessionStorage so it only
// lives in that tab and survives reloads.
const impersonationKey = 'impersonation';

export interface Impersonation {
    firstName: string;
    lastName: string;
}

export const saveImpersonation = (impersonation: Impersonation): void => {
    sessionStorage.setItem(impersonationKey, JSON.stringify(impersonation));
};

export const getImpersonation = (): Impersonation | null => {
    try {
        const storedImpersonation = sessionStorage.getItem(impersonationKey);

        return storedImpersonation ? (JSON.parse(storedImpersonation) as Impersonation) : null;
    } catch (error) {
        clearImpersonation();
        return null;
    }
};

export const clearImpersonation = (): void => {
    sessionStorage.removeItem(impersonationKey);
};

export const setTimeZoneInStorage = (timeZone: string, marketplaceId: number): void => {
    const sessionAuth = getSessionAuth();
    const localAuth = getLocalAuth();

    if (sessionAuth && sessionAuth.marketplaceId === marketplaceId) {
        sessionAuth.timeZone = timeZone;
        setSessionAuth(sessionAuth);
    }

    if (localAuth && localAuth.marketplaceId === marketplaceId) {
        localAuth.timeZone = timeZone;
        setLocalAuth(localAuth);
    }
};

export const setRoleInStorage = (role: Role, staffId: number): void => {
    const sessionAuth = getSessionAuth();
    const localAuth = getLocalAuth();

    if (sessionAuth && sessionAuth.user && sessionAuth.user.staffId === staffId) {
        sessionAuth.user.role = { ...role };
        setSessionAuth(sessionAuth);
    }

    if (localAuth && localAuth.user && localAuth.user.staffId === staffId) {
        localAuth.user.role = { ...role };
        setLocalAuth(localAuth);
    }
};

export const changeAvatarInStorage = (avatar: string, staffId: number): void => {
    const sessionAuth = getSessionAuth();
    const localAuth = getLocalAuth();

    if (sessionAuth && sessionAuth.user?.staffId === staffId) {
        sessionAuth.user!.avatar = avatar;
        setSessionAuth(sessionAuth);
    }

    if (localAuth && localAuth.user?.staffId === staffId) {
        localAuth.user!.avatar = avatar;
        setLocalAuth(localAuth);
    }
};

export const changeSessionMarketplace = (authMarketplace: AuthMarketplace) => {
    const sessionAuth = getSessionAuth();
    sessionAuth!.marketplaceId = authMarketplace.id;
    sessionAuth!.addressId = authMarketplace.addressId;
    sessionAuth!.timeZone = authMarketplace.timeZone;
    sessionAuth!.user!.role = { ...authMarketplace.role };
    setSessionAuth(sessionAuth!);
};
